<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { TEXT_LIMIT } from '../config';

const props = defineProps({
  field: { type: Object, required: true },
  i18nPrefix: { type: String, required: true },
});

const modelValue = defineModel({ type: [String, Number, Boolean], default: '' });

const { t } = useI18n();
const store = useStore();
const teams = useMapGetter('teams/getTeams');

const fieldKey = computed(() => `${props.i18nPrefix}.FIELDS.${props.field.name.toUpperCase()}`);
const label = computed(() => t(`${fieldKey.value}.LABEL`) + (props.field.required ? ' *' : ''));
const placeholder = computed(() => t(`${fieldKey.value}.PLACEHOLDER`));
const teamOptions = computed(() => [
  { value: '', label: t('KNOWLEDGE.AI.NO_TEAM') },
  ...teams.value.map(team => ({ value: team.id, label: team.name })),
]);

onMounted(() => {
  if (props.field.kind === 'team') store.dispatch('teams/get');
});
</script>

<template>
  <div v-if="field.kind === 'switch'" class="flex items-center justify-between gap-4 py-2">
    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">{{ label }}</span>
      <span class="text-sm text-n-slate-11">{{ placeholder }}</span>
    </div>
    <Switch v-model="modelValue" />
  </div>
  <div v-else-if="field.kind === 'team'" class="flex flex-col gap-1">
    <label class="text-sm font-medium text-n-slate-12">{{ label }}</label>
    <Select v-model="modelValue" :options="teamOptions" :aria-label="label" />
    <span class="text-sm text-n-slate-11">{{ placeholder }}</span>
  </div>
  <TextArea
    v-else-if="field.kind === 'textarea'"
    v-model="modelValue"
    :label="label"
    :placeholder="placeholder"
    :max-length="TEXT_LIMIT"
    auto-height
    resize
  />
  <Input v-else v-model="modelValue" :label="label" :placeholder="placeholder" />
</template>
